<template>
  <div class="page-content almacen-page">
    <div class="almacen-wrapper">
      <div class="almacen-card">
        <form @submit.prevent="guardarCambios">
          <!-- Header -->
          <header class="entrada-header">
            <div>
              <p class="almacen-title">
                Editar orden de entrega
                <span v-if="folioFormateado" class="entrada-folio">
                  · {{ folioFormateado }}
                </span>
              </p>
              <p class="almacen-subtitle">
                Ajusta datos del encabezado, unidades incluidas y observaciones.
              </p>
              <p v-if="tipoEdicion === 'ajuste'" class="entrada-subtitle-ajuste">
                Esta edición se realiza en respuesta a una solicitud de ajuste.
              </p>
            </div>

            <div class="entrada-header__actions">
                <button
                    type="button"
                    class="ios-ghost-button"
                    @click="abrirHistorial"
                >
                    Historial
                </button>
              <button
                type="button"
                class="ios-ghost-button"
                @click="volverListado"
              >
                ← Volver al listado
              </button>
              <button
                type="submit"
                class="ios-primary-button"
                :disabled="guardando || !puedeGuardar"
              >
                {{ guardando ? 'Guardando cambios...' : 'Guardar cambios' }}
              </button>
            </div>
          </header>

          <!-- META / ENCABEZADO -->
          <section class="entrada-meta">
            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow entrada-field--centro">
                <label class="entrada-label">Centro de activación</label>
                <div
                  class="centro-picker"
                  @focusin="abrirCentroDropdown"
                  @focusout="programarCierreCentroDropdown"
                >
                  <input
                    v-model.trim="centroBusqueda"
                    type="text"
                    class="entrada-input centro-picker__input"
                    placeholder="Busca por nombre, dirección, # tienda o retailer"
                    autocomplete="off"
                    @input="onCentroBusquedaInput"
                    @keydown.esc="dropdownCentrosAbierto = false"
                  />

                  <button
                    v-if="encabezado.id_centro || centroBusqueda"
                    type="button"
                    class="centro-picker__clear"
                    @mousedown.prevent
                    @click="limpiarCentroSeleccion"
                  >
                    ✕
                  </button>

                  <div
                    v-if="dropdownCentrosAbierto"
                    class="centro-picker__dropdown"
                  >
                    <div
                      v-if="!centrosFiltrados.length"
                      class="centro-picker__empty"
                    >
                      No se encontraron centros con ese filtro.
                    </div>

                    <button
                      v-for="centro in centrosFiltrados"
                      :key="centro.id_centro"
                      type="button"
                      class="centro-picker__option"
                      :class="{ 'centro-picker__option--active': String(encabezado.id_centro) === String(centro.id_centro) }"
                      @mousedown.prevent="seleccionarCentro(centro)"
                    >
                      <div class="centro-picker__option-title">
                        {{ centro.nombre || 'Sin nombre' }}
                      </div>
                      <div class="centro-picker__option-meta">
                        {{ centroEtiquetaSecundaria(centro) || 'Sin datos complementarios' }}
                      </div>
                    </button>
                  </div>
                </div>

                <div
                  v-if="centroSeleccionado"
                  class="centro-picker__selected"
                >
                  <span class="centro-picker__selected-title">{{ centroSeleccionado.nombre }}</span>
                  <span class="centro-picker__selected-meta">{{ centroEtiquetaSecundaria(centroSeleccionado) }}</span>
                </div>
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Fecha programada</label>
                <input
                  type="date"
                  v-model="encabezado.fecha_programada"
                  class="entrada-input"
                />
              </div>
            </div>

            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow">
                <label class="entrada-label">Contacto</label>
                <input
                  type="text"
                  v-model="encabezado.contacto_nombre"
                  class="entrada-input"
                  placeholder="Nombre de la persona que recibe"
                />
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Teléfono</label>
                <input
                  type="text"
                  v-model="encabezado.contacto_telefono"
                  class="entrada-input"
                  placeholder="Teléfono de contacto"
                />
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Correo</label>
                <input
                  type="email"
                  v-model="encabezado.contacto_email"
                  class="entrada-input"
                  placeholder="Correo de contacto"
                />
              </div>
            </div>

            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow">
                <label class="entrada-label">Observaciones generales</label>
                <textarea
                  v-model="encabezado.observaciones"
                  class="entrada-input entrada-input--textarea"
                  rows="2"
                  placeholder="Notas generales de esta orden de entrega..."
                ></textarea>
              </div>
            </div>
          </section>

          <!-- MOTIVO DE EDICIÓN / AJUSTE -->
          <section class="entrada-meta entrada-meta--motivo">
            <div class="entrada-field entrada-field--grow">
              <label class="entrada-label">
                Motivo de esta edición
                <span class="entrada-label-hint">
                  (para historial de versiones)
                </span>
              </label>
              <textarea
                v-model="motivoEdicion"
                class="entrada-input entrada-input--textarea"
                rows="2"
                :placeholder="placeholderMotivo"
              ></textarea>
            </div>
          </section>

          <!-- UNIDADES ACTUALES -->
          <section class="entrada-items">
            <div class="entrada-item-card entrada-item-card--resumen">
              <div class="entrada-item-header">
                <span class="entrada-item-badge">
                  Unidades actualmente en la orden
                </span>
              </div>

              <div v-if="!detallesExistentes.length" class="entrada-hint">
                No hay unidades asociadas a esta orden. Agrega al menos una unidad principal.
              </div>

              <div v-else class="orden-editar-tabla-wrapper">
                <table class="orden-editar-tabla">
                  <thead>
                    <tr>
                      <th style="width: 60px;">Grupo</th>
                      <th style="width: 90px;">Tipo</th>
                      <th>Producto</th>
                      <th style="width: 180px;">Número de serie</th>
                      <th>Observaciones</th>
                      <th style="width: 70px;">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(d, index) in detallesExistentes"
                      :key="d.id_detalle || d.uid"
                    >
                      <td class="orden-editar-center">
                        {{ d.grupo_unidad || '—' }}
                      </td>
                      <td class="orden-editar-center">
                        {{ d.tipo_unidad === 'complemento' ? 'Complemento' : 'Principal' }}
                      </td>
                      <td>
                        {{ d.producto_nombre || '—' }}
                      </td>
                      <td>
                        {{ d.numero_serie || '—' }}
                      </td>
                      <td>
                        <textarea
                          v-model="d.observaciones"
                          class="entrada-input entrada-input--textarea orden-editar-observaciones"
                          rows="2"
                          placeholder="Observaciones específicas de esta unidad..."
                        ></textarea>
                      </td>
                      <td class="orden-editar-center">
                        <button
                          type="button"
                          class="orden-editar-remove"
                          title="Marcar para eliminar de la orden"
                          @click="marcarParaEliminar(index)"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- UNIDADES A ELIMINAR -->
            <div
              v-if="detallesEliminados.length"
              class="entrada-item-card entrada-item-card--warning"
            >
              <div class="entrada-item-header">
                <span class="entrada-item-badge entrada-item-badge--warning">
                  Unidades que se eliminarán de la orden
                </span>
              </div>

              <p class="entrada-hint">
                Al guardar, estas unidades regresarán a estado <strong>almacén</strong>.
              </p>

              <div class="orden-editar-tabla-wrapper">
                <table class="orden-editar-tabla orden-editar-tabla--compact">
                  <thead>
                    <tr>
                      <th style="width: 60px;">Grupo</th>
                      <th style="width: 90px;">Tipo</th>
                      <th>Producto</th>
                      <th style="width: 180px;">Número de serie</th>
                      <th style="width: 90px;">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(d, index) in detallesEliminados"
                      :key="'del-' + (d.id_detalle || d.uid)"
                    >
                      <td class="orden-editar-center">
                        {{ d.grupo_unidad || '—' }}
                      </td>
                      <td class="orden-editar-center">
                        {{ d.tipo_unidad === 'complemento' ? 'Complemento' : 'Principal' }}
                      </td>
                      <td>{{ d.producto_nombre || '—' }}</td>
                      <td>{{ d.numero_serie || '—' }}</td>
                      <td class="orden-editar-center">
                        <button
                          type="button"
                          class="orden-editar-undo"
                          @click="deshacerEliminado(index)"
                        >
                          ↩ Deshacer
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- AGREGAR NUEVOS EQUIPOS -->
            <div class="entrada-item-card entrada-item-card--selector">
              <div class="entrada-item-header">
                <span class="entrada-item-badge">
                  Agregar nuevas unidades a la orden
                </span>
              </div>

              <div class="entrada-item-row">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Producto</label>
                  <select
                    v-model="productoSeleccionadoId"
                    class="entrada-input"
                    @change="onProductoChange"
                  >
                    <option value="">Seleccione un producto...</option>
                    <option
                      v-for="p in productos"
                      :key="p.id_producto"
                      :value="p.id_producto"
                    >
                      {{ p.nombre }} ({{ p.unidades_disponibles }} en almacén)
                    </option>
                  </select>
                </div>

                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Unidad / paquete</label>
                  <select
                    v-model="paqueteSeleccionadoId"
                    class="entrada-input"
                    :disabled="!paquetesDisponibles.length || cargandoPaquetes"
                  >
                    <option value="">
                      {{
                        cargandoPaquetes
                          ? 'Cargando unidades...'
                          : 'Seleccione unidad disponible...'
                      }}
                    </option>
                    <option
                      v-for="p in paquetesDisponibles"
                      :key="p.id_unidad_principal"
                      :value="p.id_unidad_principal"
                    >
                      {{ etiquetaPaquete(p) }}
                    </option>
                  </select>
                </div>

                <div class="entrada-field entrada-field--acciones">
                  <label class="entrada-label">&nbsp;</label>
                  <button
                    type="button"
                    class="ios-primary-button"
                    :disabled="!paqueteSeleccionadoId"
                    @click="agregarEquipoDesdeSeleccion"
                  >
                    + Añadir a la orden
                  </button>
                </div>
              </div>
            </div>

            <!-- EQUIPOS NUEVOS AGREGADOS EN ESTA EDICIÓN -->
            <div
              v-for="(item, index) in equiposNuevos"
              :key="item.uid"
              class="entrada-item-card"
            >
              <div class="entrada-item-header">
                <span class="entrada-item-badge">
                  Equipo nuevo #{{ index + 1 }}
                </span>
                <button
                  type="button"
                  class="entrada-item-remove"
                  @click="eliminarEquipoNuevo(index)"
                >
                  Eliminar
                </button>
              </div>

              <div class="entrada-item-row">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Producto</label>
                  <div class="entrada-resumen-text">
                    {{ item.producto.nombre }}
                  </div>
                </div>

                <div class="entrada-field">
                  <label class="entrada-label">Serie principal</label>
                  <div class="entrada-resumen-text">
                    {{ item.serie_principal }}
                  </div>
                </div>
              </div>

              <div class="entrada-item-row" v-if="item.complementos.length">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Complementos</label>
                  <ul class="entrada-complementos-list">
                    <li
                      v-for="c in item.complementos"
                      :key="c.id_unidad"
                    >
                      <strong>{{ c.nombre_producto }}:</strong>
                      <span>{{ c.numero_serie }}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="entrada-item-row">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Observaciones del equipo</label>
                  <textarea
                    v-model="item.observaciones"
                    class="entrada-input entrada-input--textarea"
                    rows="2"
                    placeholder="Notas específicas de este equipo en la orden..."
                  ></textarea>
                </div>
              </div>
            </div>

            <div
              v-if="!detallesExistentes.length && !equiposNuevos.length"
              class="entrada-hint"
            >
              La orden no tendría unidades. Agrega al menos una unidad antes de guardar.
            </div>
          </section>
        </form>
      </div>
      <ModalHistorialOrdenEntrega
        :id-orden="idOrden"
        :visible="mostrarHistorial"
        @close="cerrarHistorial"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ModalHistorialOrdenEntrega from './ModalHistorialOrdenEntrega.vue';

export default {
  name: 'DashboardEditarOrdenEntrega',
    components: {
    ModalHistorialOrdenEntrega,
  },
  data() {
    const hoy = new Date().toISOString().slice(0, 10);

    return {
      idOrden: Number(this.$route.params.id || 0),

      encabezado: {
        id_centro: '',
        fecha_programada: hoy,
        contacto_nombre: '',
        contacto_telefono: '',
        contacto_email: '',
        observaciones: '',
      },

      centros: [],
      productos: [],

      // Unidades existentes
      detallesExistentes: [],
      detallesEliminados: [],

      // Nuevos equipos (igual estructura que en crear)
      productoSeleccionadoId: '',
      paquetesDisponibles: [],
      paqueteSeleccionadoId: '',
      equiposNuevos: [],

      cargandoOrden: false,
      cargandoCatalogos: false,
      cargandoPaquetes: false,
      guardando: false,
      centroBusqueda: '',
      dropdownCentrosAbierto: false,
      centroCloseTimer: null,

      // Historial / edición
      tipoEdicion: this.$route.query.tipo || 'manual', // 'manual' | 'ajuste'
      idAjuste: this.$route.query.idAjuste || null,
      motivoEdicion: '',

      // Datos de folio para visual
      folio: null,
      numeroVersion: 1,

    // Modal historial
      mostrarHistorial: false,
    };
  },
  computed: {
    userToken() {
      return this.$store.state.user.token;
    },
    centroSeleccionado() {
      return (this.centros || []).find((c) => String(c.id_centro) === String(this.encabezado.id_centro)) || null;
    },
    centrosFiltrados() {
      const lista = Array.isArray(this.centros) ? this.centros : [];
      const texto = this.normalizarTexto(this.centroBusqueda || '');
      if (!texto) return lista.slice(0, 25);

      const tokens = texto.split(/\s+/).filter(Boolean);
      return lista
        .filter((c) => {
          const haystack = this.centroBusquedaTexto(c);
          return tokens.every((token) => haystack.includes(token));
        })
        .slice(0, 25);
    },
    puedeGuardar() {
      return (
        !this.guardando &&
        this.encabezado.id_centro &&
        this.encabezado.fecha_programada &&
        (this.detallesExistentes.length > 0 || this.equiposNuevos.length > 0)
      );
    },
    placeholderMotivo() {
      if (this.tipoEdicion === 'ajuste') {
        return 'Describe brevemente cómo se corrige la orden según el ajuste solicitado...';
      }
      return 'Motivo interno de esta edición (ej. corrección de datos, cambio de centro, etc.)';
    },
    folioFormateado() {
      if (!this.folio && this.folio !== 0) return '';
      const n = parseInt(this.folio, 10);
      if (Number.isNaN(n)) return '';
      const base = 'ENT-' + String(n).padStart(4, '0');
      if (!this.numeroVersion || this.numeroVersion <= 1) return base;
      return `${base}-${this.numeroVersion}`;
    },
  },
  mounted() {
    this.cargarCatalogosYOrden();
  },
  methods: {
    normalizarTexto(valor) {
      return String(valor || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },
    centroBusquedaTexto(centro) {
      return this.normalizarTexto([
        centro?.nombre,
        centro?.direccion,
        centro?.retailer_nombre,
        centro?.numero_tienda,
        centro?.ciudad,
        centro?.estado,
        centro?.pais,
      ]
        .filter(Boolean)
        .join(' '));
    },
    centroEtiquetaPrincipal(centro) {
      return centro?.nombre || '';
    },
    centroEtiquetaSecundaria(centro) {
      const partes = [];
      if (centro?.direccion) partes.push(centro.direccion);
      else {
        const ubicacion = [centro?.ciudad, centro?.estado].filter(Boolean).join(', ');
        if (ubicacion) partes.push(ubicacion);
      }
      if (centro?.numero_tienda) partes.push('#' + centro.numero_tienda);
      if (centro?.retailer_nombre) partes.push(centro.retailer_nombre);
      return partes.join(' · ');
    },
    cancelarCierreCentroDropdown() {
      if (this.centroCloseTimer) {
        clearTimeout(this.centroCloseTimer);
        this.centroCloseTimer = null;
      }
    },
    programarCierreCentroDropdown() {
      this.cancelarCierreCentroDropdown();
      this.centroCloseTimer = setTimeout(() => {
        this.dropdownCentrosAbierto = false;
      }, 140);
    },
    abrirCentroDropdown() {
      this.cancelarCierreCentroDropdown();
      this.dropdownCentrosAbierto = true;
    },
    onCentroBusquedaInput() {
      this.abrirCentroDropdown();
      if (
        this.centroSeleccionado &&
        this.centroBusqueda !== this.centroEtiquetaPrincipal(this.centroSeleccionado)
      ) {
        this.encabezado.id_centro = '';
      }
    },
    sincronizarCentroBusqueda() {
      this.centroBusqueda = this.centroSeleccionado
        ? this.centroEtiquetaPrincipal(this.centroSeleccionado)
        : '';
    },
    seleccionarCentro(centro) {
      this.encabezado.id_centro = String(centro.id_centro);
      this.centroBusqueda = this.centroEtiquetaPrincipal(centro);
      this.dropdownCentrosAbierto = false;
    },
    limpiarCentroSeleccion() {
      this.encabezado.id_centro = '';
      this.centroBusqueda = '';
      this.abrirCentroDropdown();
    },
    abrirHistorial() {
        this.mostrarHistorial = true;
    },
    cerrarHistorial() {
        this.mostrarHistorial = false;
    },
    async cargarCatalogosYOrden() {
      if (!this.idOrden) {
        this.$toast?.error('ID de orden inválido.');
        this.volverListado();
        return;
      }

      this.cargandoCatalogos = true;
      this.cargandoOrden = true;

      try {
        const [resCatalogos, resOrden] = await Promise.all([
          axios.get('/api/almacen/ordenes-entrega/catalogos'),
          axios.get('/api/almacen/ordenes-entrega/ver/' + this.idOrden),
        ]);

        // Catálogos
        if (resCatalogos.data?.status === 'ok') {
          this.centros = Array.isArray(resCatalogos.data.centros)
            ? resCatalogos.data.centros
            : [];
          this.productos = Array.isArray(resCatalogos.data.productos)
            ? resCatalogos.data.productos
            : [];
          this.sincronizarCentroBusqueda();
        } else {
          this.centros = [];
          this.productos = [];
          this.sincronizarCentroBusqueda();
        }

        // Orden
        if (resOrden.data?.status !== 'ok' || !resOrden.data.orden) {
          this.$toast?.error('No se encontró la orden de entrega.');
          this.volverListado();
          return;
        }

        const orden = resOrden.data.orden;

        // Encabezado editable
        this.encabezado = {
          id_centro: String(orden.id_centro || ''),
          fecha_programada: orden.fecha_programada || new Date().toISOString().slice(0, 10),
          contacto_nombre: orden.contacto_nombre || '',
          contacto_telefono: orden.contacto_telefono || '',
          contacto_email: orden.contacto_email || '',
          observaciones: orden.observaciones || '',
        };

        this.folio = orden.folio || orden.id_orden || null;
        this.numeroVersion = orden.numero_version ? Number(orden.numero_version) : 1;
        this.sincronizarCentroBusqueda();

        // Unidades existentes
        const detalles = Array.isArray(orden.detalles) ? orden.detalles : [];

        this.detallesExistentes = detalles.map((d) => ({
          id_detalle: d.id_detalle,
          id_unidad: d.id_unidad,
          grupo_unidad: d.grupo_unidad || d.grupo || '',
          tipo_unidad: d.tipo_unidad || 'principal',
          producto_nombre: d.producto_nombre || d.nombre_producto || '',
          numero_serie: d.numero_serie || '',
          entregado: d.entregado,
          devuelto: d.devuelto,
          observaciones: d.observaciones || '',
          esNuevo: false,
        }));

        this.detallesEliminados = [];

        // Si la edición viene desde un ajuste aprobado, podrías precargar aquí el motivo original
        if (this.tipoEdicion === 'ajuste' && orden.motivo_ajuste) {
          this.motivoEdicion = orden.motivo_ajuste;
        }
      } catch (error) {
        console.error('Error al cargar datos de edición:', error);
        this.$toast?.error('No se pudieron cargar los datos de la orden.');
        this.volverListado();
      } finally {
        this.cargandoCatalogos = false;
        this.cargandoOrden = false;
      }
    },

    // --- Unidades existentes ---
    marcarParaEliminar(index) {
      const item = this.detallesExistentes.splice(index, 1)[0];
      if (item) {
        this.detallesEliminados.push(item);
      }
    },

    deshacerEliminado(index) {
      const item = this.detallesEliminados.splice(index, 1)[0];
      if (item) {
        this.detallesExistentes.push(item);
      }
    },

    // --- Selector de nuevos equipos (igual que en crear) ---
    onProductoChange() {
      this.paquetesDisponibles = [];
      this.paqueteSeleccionadoId = '';
      if (!this.productoSeleccionadoId) return;
      this.cargarPaquetesProducto(this.productoSeleccionadoId);
    },

    async cargarPaquetesProducto(idProducto) {
      this.cargandoPaquetes = true;
      try {
        const res = await axios.get(
          '/api/almacen/ordenes-entrega/unidades-disponibles/' + idProducto
        );

        if (res.data?.status === 'ok') {
          this.paquetesDisponibles = Array.isArray(res.data.paquetes)
            ? res.data.paquetes
            : [];
        } else {
          this.paquetesDisponibles = [];
        }

        if (!this.paquetesDisponibles.length) {
          this.$toast?.info(
            'No hay unidades disponibles en almacén para ese producto.'
          );
        }
      } catch (error) {
        console.error('Error al cargar unidades disponibles:', error);
        this.$toast?.error('No se pudieron cargar las unidades disponibles.');
        this.paquetesDisponibles = [];
      } finally {
        this.cargandoPaquetes = false;
      }
    },

    etiquetaPaquete(p) {
      const compPart =
        p.complementos && p.complementos.length
          ? ` + ${p.complementos.length} complemento(s)`
          : ' (sin complementos)';
      const ubicacion = p.ubicacion_actual ? ` · ${p.ubicacion_actual}` : '';

      return `Serie ${p.serie_principal}${compPart}${ubicacion}`;
    },

    agregarEquipoDesdeSeleccion() {
      if (!this.paqueteSeleccionadoId) return;

      const paquete = this.paquetesDisponibles.find(
        (p) =>
          Number(p.id_unidad_principal) === Number(this.paqueteSeleccionadoId)
      );

      if (!paquete) {
        this.$toast?.error('No se encontró la unidad seleccionada.');
        return;
      }

      // Evitar duplicados dentro de equiposNuevos
      const yaExisteNuevo = this.equiposNuevos.some(
        (it) =>
          Number(it.id_unidad_principal) ===
          Number(paquete.id_unidad_principal)
      );
      if (yaExisteNuevo) {
        this.$toast?.info('Ese paquete ya está agregado como nuevo en la orden.');
        return;
      }

      const item = {
        uid:
          'nuevo-' +
          Date.now() +
          '-' +
          Math.random().toString(36).slice(2, 8),
        producto: {
          id_producto: paquete.producto.id_producto,
          nombre: paquete.producto.nombre,
        },
        id_unidad_principal: paquete.id_unidad_principal,
        serie_principal: paquete.serie_principal,
        complementos: (paquete.complementos || []).map((c) => ({
          id_unidad: c.id_unidad,
          numero_serie: c.numero_serie,
          nombre_producto: c.nombre_producto,
        })),
        observaciones: '',
      };

      this.equiposNuevos.push(item);
    },

    eliminarEquipoNuevo(index) {
      this.equiposNuevos.splice(index, 1);
    },

    // --- Guardar cambios ---
    async guardarCambios() {
      if (!this.puedeGuardar) {
        this.$toast?.error(
          'Verifica centro, fecha programada y que exista al menos una unidad.'
        );
        return;
      }

      if (!this.motivoEdicion.trim()) {
        this.$toast?.error('Indica el motivo de esta edición para el historial.');
        return;
      }

      this.guardando = true;

      try {
        const payload = {
          id_orden: this.idOrden,
          encabezado: this.encabezado,

          // Detalles que siguen en la orden (se actualizan observaciones)
          detalles_existentes: this.detallesExistentes.map((d) => ({
            id_detalle: d.id_detalle,
            observaciones: d.observaciones || '',
          })),

          // Detalles que se eliminan de la orden (se deben regresar a almacén)
          detalles_eliminados: this.detallesEliminados.map((d) => ({
            id_detalle: d.id_detalle,
            id_unidad: d.id_unidad,
          })),

          // Nuevos equipos a insertar (igual estructura que en crear)
          equipos_nuevos: this.equiposNuevos.map((item) => ({
            id_unidad_principal: item.id_unidad_principal,
            complementos: item.complementos.map((c) => c.id_unidad),
            observaciones: item.observaciones || '',
          })),

          // Datos para versionado / historial
          tipo_edicion: this.tipoEdicion, // 'manual' | 'ajuste'
          id_ajuste: this.idAjuste,
          motivo_edicion: this.motivoEdicion.trim(),

          tokenUser: this.userToken,
        };

        const res = await axios.post(
          '/api/almacen/ordenes-entrega/actualizar',
          payload
        );

        if (res.data?.status === 'ok') {
          this.$toast?.success('Orden de entrega actualizada correctamente.');
          // Podemos recargar o volver al detalle
          this.$router.push({ name: 'DashboardOrdenesEntrega' });
        } else {
          this.$toast?.error(
            res.data?.message || 'No se pudieron guardar los cambios de la orden.'
          );
        }
      } catch (error) {
        console.error('Error al actualizar orden de entrega:', error);
        const msg =
          error.response?.data?.message ||
          'Ocurrió un error al guardar los cambios de la orden.';
        this.$toast?.error(msg);
      } finally {
        this.guardando = false;
      }
    },

    volverListado() {
      this.$router.push({ name: 'DashboardOrdenesEntrega' });
    },
  },
};
</script>

<style scoped>
.almacen-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.almacen-wrapper {
  width: 100%;
  max-width: 1100px;
}

.almacen-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.almacen-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.entrada-folio {
  font-size: 0.8rem;
  font-weight: 400;
  color: #9ca3af;
}

.almacen-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

.entrada-subtitle-ajuste {
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: #fde68a;
}

/* Header */
.entrada-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

.entrada-header__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* Meta */
.entrada-meta {
  border-radius: 1.25rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.92);
  padding: 0.7rem 0.7rem 0.6rem;
  margin-bottom: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.entrada-meta--motivo {
  background: radial-gradient(
      circle at 0 0,
      rgba(251, 191, 36, 0.18),
      rgba(15, 23, 42, 0.96)
    );
  border-color: rgba(251, 191, 36, 0.55);
}

.entrada-label-hint {
  font-size: 0.7rem;
  color: #e5e7eb;
  opacity: 0.9;
}

.entrada-meta__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

/* Items */
.entrada-items {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.entrada-item-card {
  border-radius: 1.3rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.7rem 0.7rem 0.6rem;
}

.entrada-item-card--selector {
  border-style: dashed;
}

.entrada-item-card--warning {
  border-color: rgba(248, 113, 113, 0.85);
  background: radial-gradient(
      circle at 0 0,
      rgba(248, 113, 113, 0.2),
      rgba(15, 23, 42, 0.96)
    );
}

.entrada-item-card--resumen {
  border-color: rgba(129, 140, 248, 0.7);
}

/* Inputs */
.entrada-field {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  min-width: 0;
}

.entrada-field--grow {
  flex: 1 1 0;
}

.entrada-field--acciones {
  display: flex;
  flex-direction: column;
}

.entrada-label {
  font-size: 0.78rem;
  color: #cbd5f5;
}

.entrada-input {
  border-radius: 0.8rem;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.4rem 0.7rem;
  font-size: 0.82rem;
  color: #e5e7eb;
  outline: none;
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.entrada-input--textarea {
  resize: vertical;
}

.entrada-input::placeholder {
  color: #6b7280;
}

.entrada-input:focus {
  border-color: rgba(129, 140, 248, 0.95);
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.7);
}

/* Item */
.entrada-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}

.entrada-item-badge {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.96);
}

.entrada-item-badge--warning {
  border-color: rgba(248, 113, 113, 0.9);
  color: #fecaca;
}

.entrada-item-remove {
  border: none;
  background: transparent;
  color: #fca5a5;
  font-size: 0.78rem;
  cursor: pointer;
}

.entrada-item-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.4rem;
}

/* Complementos */
.entrada-complementos-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.entrada-resumen-text {
  font-size: 0.8rem;
  color: #e5e7eb;
}

/* Hints */
.entrada-hint {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Tabla de edición */
.orden-editar-tabla-wrapper {
  overflow-x: auto;
  margin-top: 0.4rem;
}

.orden-editar-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.76rem;
}

.orden-editar-tabla th,
.orden-editar-tabla td {
  border: 1px solid rgba(148, 163, 184, 0.6);
  padding: 0.25rem 0.35rem;
  vertical-align: top;
}

.orden-editar-tabla thead th {
  background: rgba(15, 23, 42, 0.96);
  font-weight: 600;
}

.orden-editar-tabla--compact th,
.orden-editar-tabla--compact td {
  padding: 0.22rem 0.3rem;
}

.orden-editar-center {
  text-align: center;
}

.orden-editar-observaciones {
  min-width: 180px;
}

.orden-editar-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1;
  color: #fca5a5;
}

.orden-editar-undo {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.2rem 0.5rem;
  font-size: 0.72rem;
  color: #e5e7eb;
  cursor: pointer;
}

/* Botones iOS */
.ios-primary-button,
.ios-ghost-button {
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.ios-primary-button {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
  color: #e5e7eb;
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.7);
}

.ios-primary-button:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 18px 38px rgba(37, 99, 235, 0.9);
}

.ios-ghost-button {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.ios-ghost-button:hover {
  background: rgba(15, 23, 42, 1);
}

/* Responsive */
@media (max-width: 768px) {
  .entrada-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .entrada-meta__row {
    flex-direction: column;
  }

  .entrada-item-row {
    flex-direction: column;
  }

  .orden-editar-observaciones {
    min-width: 140px;
  }
}
.entrada-field--centro {
  position: relative;
}

.centro-picker {
  position: relative;
}

.centro-picker__input {
  padding-right: 2.4rem;
}

.centro-picker__clear {
  position: absolute;
  top: 50%;
  right: 0.7rem;
  transform: translateY(-50%);
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.88);
  color: #cbd5e1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.centro-picker__dropdown {
  position: absolute;
  z-index: 30;
  top: calc(100% + 0.45rem);
  left: 0;
  right: 0;
  max-height: 320px;
  overflow-y: auto;
  padding: 0.45rem;
  border-radius: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.98);
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.55);
}

.centro-picker__dropdown::-webkit-scrollbar {
  width: 8px;
}

.centro-picker__dropdown::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 999px;
}

.centro-picker__dropdown::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.65);
  border-radius: 999px;
}

.centro-picker__empty {
  padding: 0.8rem 0.9rem;
  color: #94a3b8;
  font-size: 0.8rem;
}

.centro-picker__option {
  width: 100%;
  text-align: left;
  padding: 0.75rem 0.8rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(2, 6, 23, 0.16);
  color: #e5e7eb;
  cursor: pointer;
  display: grid;
  gap: 0.18rem;
}

.centro-picker__option + .centro-picker__option {
  margin-top: 0.35rem;
}

.centro-picker__option:hover,
.centro-picker__option--active {
  border-color: rgba(96, 165, 250, 0.32);
  background: rgba(30, 41, 59, 0.78);
}

.centro-picker__option-title {
  font-size: 0.84rem;
  font-weight: 700;
  color: #f8fafc;
}

.centro-picker__option-meta {
  font-size: 0.74rem;
  color: #94a3b8;
  line-height: 1.35;
}

.centro-picker__selected {
  margin-top: 0.45rem;
  display: grid;
  gap: 0.12rem;
  padding: 0.55rem 0.75rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(96, 165, 250, 0.22);
  background: rgba(30, 41, 59, 0.58);
}

.centro-picker__selected-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #e5e7eb;
}

.centro-picker__selected-meta {
  font-size: 0.73rem;
  color: #94a3b8;
  line-height: 1.35;
}

</style>
